import type { Position, Certification } from "../../types";

type TooltipContent =
  | { kind: "position"; position: Position }
  | { kind: "certification"; certification: Certification }
  | { kind: "text"; title: string; subtitle?: string; body?: string };

interface TooltipProps {
  content: TooltipContent | null;
  anchor: { x: number; y: number } | null;
}

export function Tooltip({ content, anchor }: TooltipProps) {
  if (!content || !anchor) return null;

  const style: React.CSSProperties = {
    position: "fixed",
    left: anchor.x + 18,
    top: anchor.y + 18,
    pointerEvents: "none",
    zIndex: 50,
  };

  return (
    <div
      style={style}
      className="max-w-md"
    >
      <div
        className="relative px-5 py-4 backdrop-blur-md"
        style={{
          background: "rgba(255, 255, 255, 0.96)",
          borderTop: "2px solid var(--color-copper)",
          borderBottom: "1px solid var(--color-rule)",
          boxShadow:
            "0 18px 40px -12px rgba(13, 13, 12, 0.18), 0 0 0 1px var(--color-rule-soft)",
        }}
      >
        {content.kind === "position" && (
          <>
            <div className="kicker mb-2">{content.position.company}</div>
            <div
              className="display text-[19px] leading-[1.15] mb-2"
              style={{ color: "var(--color-cream)", fontWeight: 400, letterSpacing: "-0.015em" }}
            >
              {content.position.title}
            </div>
            <div className="mono text-[10px] flex items-center gap-2 mb-3" style={{ color: "var(--color-cream-dim)" }}>
              <span>{content.position.startDate}</span>
              <span style={{ color: "var(--color-rule)" }}>—</span>
              <span style={{ color: content.position.endDate ? undefined : "var(--color-now)" }}>
                {content.position.endDate || "ongoing"}
              </span>
              {content.position.location && (
                <>
                  <span style={{ color: "var(--color-rule)" }}>·</span>
                  <span>{content.position.location}</span>
                </>
              )}
            </div>
            {content.position.description && (
              <div className="text-[12.5px] leading-[1.55] line-clamp-5" style={{ color: "var(--color-cream-soft)" }}>
                {content.position.description}
              </div>
            )}
          </>
        )}

        {content.kind === "certification" && (
          <>
            <div className="kicker mb-2">{content.certification.authority}</div>
            <div
              className="display text-[17px] leading-[1.2] mb-2"
              style={{ color: "var(--color-cream)", fontWeight: 400, letterSpacing: "-0.015em" }}
            >
              {content.certification.name}
            </div>
            <div className="mono text-[10px]" style={{ color: "var(--color-cream-dim)" }}>
              {content.certification.date}
            </div>
          </>
        )}

        {content.kind === "text" && (
          <>
            {content.subtitle && <div className="kicker mb-2">{content.subtitle}</div>}
            <div
              className="display text-[17px] leading-[1.2] mb-2"
              style={{ color: "var(--color-cream)", fontWeight: 400, letterSpacing: "-0.015em" }}
            >
              {content.title}
            </div>
            {content.body && (
              <div className="text-[12.5px] leading-[1.55] line-clamp-5" style={{ color: "var(--color-cream-soft)" }}>
                {content.body}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export type { TooltipContent };
